import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaSync, FaSearch, FaTimes, FaEdit } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Student {
  enrollmentId: number;
  studentId: number;
  studentFirstName: string;
  studentLastName: string;
  studentEmail: string;
  courseTitle: string;
}

interface Mentor {
  studentId: number;
  firstName: string;
  lastName: string;
}

const TOPIC_OPTIONS = ["A1", "A2", "B1", "B2", "AP", "B1 Exam Preparation"];

const EditBatch: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [zoomLink, setZoomLink] = useState("");
  const [startingDate, setStartingDate] = useState("");
  const [endingDate, setEndingDate] = useState("");
  const [mentorId, setMentorId] = useState<number | "">("");
  const [isActive, setIsActive] = useState<boolean>(true);

  const [selectedEnrollmentIds, setSelectedEnrollmentIds] = useState<number[]>(
    []
  );
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourseFilter, setSelectedCourseFilter] = useState("");
  const [availableCourses, setAvailableCourses] = useState<string[]>([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const batchRes = await fetch(`${API_BASE}/api/admin/batches/${id}`, {
        credentials: "include",
      });
      if (!batchRes.ok) throw new Error("Failed to fetch batch");
      const batch = await batchRes.json();

      setName(batch.name || "");
      setTopic(batch.topic || "");
      setZoomLink(batch.zoomLink || "");
      setStartingDate(batch.startingDate || "");
      setEndingDate(batch.endingDate || "");
      setMentorId(batch.mentorId ?? "");
      setSelectedEnrollmentIds(batch.enrollmentIds || []);
      setIsActive(batch.isActive ?? true);

      // Use the new dedicated endpoint for eligible enrollments including current batch
      const [sRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/batches/${id}/eligible-enrollments`, {
          credentials: "include",
        }),
        fetch(`${API_BASE}/api/admin/mentors`, { credentials: "include" }),
      ]);
      if (!sRes.ok || !mRes.ok)
        throw new Error("Failed to load reference data");
      const [sJson, mJson] = await Promise.all([sRes.json(), mRes.json()]);

      const validStudents: Student[] = Array.isArray(sJson)
        ? sJson.map((e: any) => ({
            enrollmentId: e.id,
            studentId: e.studentId,
            studentFirstName: e.studentFirstName,
            studentLastName: e.studentLastName,
            studentEmail: e.studentEmail,
            courseTitle: e.courseTitle,
          }))
        : [];

      setStudents(validStudents);
      setFilteredStudents(validStudents);

      // Extract unique courses
      const courses = Array.from(
        new Set(validStudents.map((s) => s.courseTitle))
      ).sort();
      setAvailableCourses(courses);

      const mentorArr: Mentor[] = Array.isArray(mJson)
        ? mJson
        : mJson.mentors || mJson.data || [];
      setMentors(mentorArr.filter((u: any) => u.role === "MENTOR"));
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Filter students based on search and course filter
  useEffect(() => {
    let filtered = students;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.studentFirstName.toLowerCase().includes(query) ||
          s.studentLastName.toLowerCase().includes(query) ||
          s.studentEmail.toLowerCase().includes(query)
      );
    }

    // Apply course filter
    if (selectedCourseFilter) {
      filtered = filtered.filter((s) => s.courseTitle === selectedCourseFilter);
    }

    setFilteredStudents(filtered);
  }, [searchQuery, selectedCourseFilter, students]);

  const handleSubmit = async () => {
    setError("");
    if (!name.trim() || !zoomLink.trim()) {
      setError("Batch name and Zoom link are required.");
      return;
    }
    try {
      const payload = {
        name,
        topic,
        zoomLink,
        startingDate,
        endingDate,
        enrollmentIds: selectedEnrollmentIds,
        mentorId: mentorId === "" ? null : mentorId,
        isActive,
      };
      const res = await fetch(`${API_BASE}/api/admin/batches/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      nav("/admin/manage-batches");
    } catch (e: any) {
      setError(e.message || "Something went wrong");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCourseFilter("");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaEdit />}
        title="Edit Batch"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Batches", to: "/admin/manage-batches" },
          { label: "Edit Batch" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-5xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-[#009fe3]/10 hover:bg-[#009fe3]/20 text-[#009fe3] rounded-lg transition-colors disabled:opacity-50"
        >
          <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white shadow-lg rounded-xl p-6 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
              <p className="text-gray-600">Loading batch details...</p>
            </div>
          ) : (
            <>
              <div className="space-y-6 mb-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Batch Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                  >
                    <option value="">Select Topic</option>
                    {TOPIC_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zoom Link <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    value={zoomLink}
                    onChange={(e) => setZoomLink(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Starting Date
                    </label>
                    <input
                      type="date"
                      value={startingDate}
                      onChange={(e) => setStartingDate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ending Date
                    </label>
                    <input
                      type="date"
                      value={endingDate}
                      onChange={(e) => setEndingDate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                </div>
              </div>

              {/* ENHANCED STUDENT SELECTION SECTION */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Enroll Students
                  <span className="ml-2 text-xs text-gray-500">
                    ({selectedEnrollmentIds.length} selected)
                  </span>
                </label>

                <div className="border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                  {/* Search and Filter Bar */}
                  <div className="p-3 sm:p-4 bg-white border-b border-gray-200 rounded-t-lg">
                    <div className="space-y-3 w-full">
                      {/* Search Input - Full Width */}
                      <div className="relative w-full">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                          type="text"
                          placeholder="Search by name or email..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent text-sm"
                          style={{ fontSize: "16px" }}
                        />
                      </div>

                      {/* Course Filter with Clear Button */}
                      <div className="flex gap-2 w-full items-stretch">
                        <select
                          value={selectedCourseFilter}
                          onChange={(e) =>
                            setSelectedCourseFilter(e.target.value)
                          }
                          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent text-sm"
                          style={{ fontSize: "16px" }}
                        >
                          <option value="">All Courses</option>
                          {availableCourses.map((course) => (
                            <option key={course} value={course}>
                              {course}
                            </option>
                          ))}
                        </select>
                        {(searchQuery || selectedCourseFilter) && (
                          <button
                            onClick={clearFilters}
                            type="button"
                            className="flex-shrink-0 w-auto px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm whitespace-nowrap"
                            title="Clear filters"
                          >
                            <FaTimes size={12} />
                            <span className="hidden sm:inline">Clear</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Student List - Increased Height */}
                  <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto p-3 sm:p-4">
                    {filteredStudents.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">
                        {students.length === 0
                          ? "No eligible students available"
                          : "No students match your filters"}
                      </p>
                    ) : (
                      filteredStudents.map((s) => (
                        <label
                          key={s.enrollmentId}
                          className="flex items-start gap-3 bg-white border border-gray-200 p-3 rounded-lg shadow-sm hover:shadow-md hover:border-[#009fe3] transition-all cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            value={s.enrollmentId}
                            checked={selectedEnrollmentIds.includes(
                              s.enrollmentId
                            )}
                            onChange={() =>
                              setSelectedEnrollmentIds((prev) =>
                                prev.includes(s.enrollmentId)
                                  ? prev.filter((id) => id !== s.enrollmentId)
                                  : [...prev, s.enrollmentId]
                              )
                            }
                            className="mt-1 w-4 h-4 text-[#009fe3] focus:ring-[#009fe3] flex-shrink-0"
                          />
                          <div className="text-sm leading-tight flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 truncate">
                              {s.studentFirstName} {s.studentLastName}
                            </div>
                            <div className="text-gray-600 text-xs mt-0.5 truncate">
                              {s.studentEmail}
                            </div>
                            <div className="text-[#009fe3] text-xs mt-1 font-medium truncate">
                              {s.courseTitle}
                            </div>
                          </div>
                        </label>
                      ))
                    )}
                  </div>

                  {/* Results Summary */}
                  <div className="p-3 bg-gray-100 border-t border-gray-200 rounded-b-lg text-xs text-gray-600">
                    Showing {filteredStudents.length} of {students.length}{" "}
                    eligible students
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Mentor (Optional)
                </label>
                <select
                  value={mentorId}
                  onChange={(e) =>
                    setMentorId(e.target.value === "" ? "" : +e.target.value)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                >
                  <option value="">(No mentor assigned)</option>
                  {mentors.map((m) => (
                    <option key={m.studentId} value={m.studentId}>
                      {m.firstName} {m.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-4 h-4 text-[#009fe3] focus:ring-[#009fe3] rounded"
                  />
                  <div>
                    <span className="font-medium text-gray-900">
                      Active Status
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Uncheck this to make the batch inactive
                    </p>
                  </div>
                </label>
              </div>

              <button
                onClick={handleSubmit}
                className="w-full bg-[#009fe3] text-white py-3 rounded-lg hover:bg-[#007cb5] transition-colors font-semibold shadow-md hover:shadow-lg"
              >
                Save Changes
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default EditBatch;
